import React, { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext, WorkspaceContext, OrgContext, ForecastContext } from '../../objects/Context';
import './Threads.css';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';
import axios from 'axios';
import { ReactComponent as PlusIcon } from '../../assets/icons/add-noFill-icon.svg';
import { ReactComponent as DatabaseIcon } from '../../assets/icons/data-icon.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/icons/arrow-right-icon.svg';
import { getCurrentModeConfig, shouldShowAction } from './helpers/modeConfigs';
import { getMessages, clearThread } from './helpers/contextManager';
import { createFileOperations } from './helpers/fileOperations';
import { createMessageHandlers } from './helpers/messageHandlers';
import { renderGreeting } from './helpers/greetingHelper';

import DataPreview from '../../components/DataPreview/DataPreview';
import MessageActions from '../../components/MessageActions/MessageActions';
import UsagePopup from '../../components/ErrorHandler/UsagePopup';
import Copilot from '../../components/Copilot/Copilot';
import Forecast from '../../components/Forecast/Forecast';
import ForecastQuestionnaire from '../../components/ForecastQuestionnaire/ForecastQuestionnaire';
import Activity from '../../components/Activity/Activity';
import '../AIDataTeam/Activity.css';
import '../Dashboards/MoreSelectionMenu.css';
import MessageFormatter from '../../components/MessageFormatter/MessageFormatter';


function TodosPanel({ checklist, forecastId, onChecklistUpdate }) {
  const [pendingIndex, setPendingIndex] = useState(null);
  const [agentOpen, setAgentOpen] = useState(true);
  const [defaultOpen, setDefaultOpen] = useState(true);

  const handleToggleItem = async (idx, currentCompleted) => {
    if (pendingIndex !== null || !forecastId) return;
    const newCompleted = !currentCompleted;

    const optimisticChecklist = checklist.map((item, i) =>
      i === idx ? { ...item, completed: newCompleted } : item
    );
    if (typeof onChecklistUpdate === 'function') onChecklistUpdate(optimisticChecklist);

    setPendingIndex(idx);
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/${forecastId}/checklist/${idx}`,
        { completed: newCompleted },
        { withCredentials: true }
      );
      if (response.data?.checklist && typeof onChecklistUpdate === 'function') {
        onChecklistUpdate(response.data.checklist);
      }
    } catch (err) {
      console.error('Failed to update checklist item:', err);
      if (typeof onChecklistUpdate === 'function') onChecklistUpdate(checklist);
    } finally {
      setPendingIndex(null);
    }
  };

  const agentItems = checklist.map((item, idx) => ({ item, idx })).filter(({ item }) => !item.default);
  const defaultItems = checklist.map((item, idx) => ({ item, idx })).filter(({ item }) => item.default);
  const agentCompleted = agentItems.filter(({ item }) => item.completed).length;
  const defaultCompleted = defaultItems.filter(({ item }) => item.completed).length;

  const renderGroup = (title, items, completedCount, isOpen, setIsOpen) => (
    <div className="todos-group">
      <button className="todos-group-header" onClick={() => setIsOpen(o => !o)}>
        <span className="todos-group-title">{title} ({completedCount} of {items.length} completed)</span>
        <svg
          className={`todos-group-arrow ${isOpen ? 'open' : ''}`}
          width="12" height="12" viewBox="0 0 12 12" fill="none"
        >
          <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {isOpen && (
        <div className="todos-panel-list">
          {items.map(({ item, idx }) => (
            <div
              key={idx}
              className={`todos-panel-item ${item.completed ? 'completed' : ''} ${pendingIndex === idx ? 'pending' : ''}`}
              onClick={() => handleToggleItem(idx, item.completed)}
            >
              <span className="todos-panel-circle">
                {item.completed ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8" cy="8" r="8" fill="#4a8af4"/>
                    <path d="M5 8L7 10L11 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8" cy="8" r="7" stroke="#C5C5C5" strokeWidth="1.5"/>
                  </svg>
                )}
              </span>
              <span className="todos-panel-text"><MessageFormatter message={item.item} /></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="todos-panel">
      {agentItems.length > 0 && renderGroup('Agent Findings', agentItems, agentCompleted, agentOpen, setAgentOpen)}
      {defaultItems.length > 0 && renderGroup('Always Review', defaultItems, defaultCompleted, defaultOpen, setDefaultOpen)}
    </div>
  );
}

const PROMPT_CATEGORIES = [
  {
    title: 'Row Management',
    prompts: [
      'Break out [account name] from Other Outflows as its own row.',
      'Roll [row name] back into Other Outflows.',
      'Combine [row A] and [row B] into a single row.'
    ],
  },
  {
    title: 'Forecast Adjustments',
    prompts: [
      'Increase [row name] by X% starting next week.',
      'Add a one-time expense of $X in week [#] for [description].',
      'Set [row name] to $X per week for the next 4 weeks.',
    ],
  },
  {
    title: 'Analysis & Insights',
    prompts: [
      'What weeks are most at-risk of a cash shortfall?',
      'What are the top 3 drivers of cash outflows?',
      'What\'s our projected cash low point and when does it hit?',
    ],
  },
  {
    title: 'Scenarios',
    prompts: [
      'What happens if revenue drops 20% for the next month?',
      'How does cash change if we purchase $x of inventory?',
      'What if DSO increases by 4 days?',
    ],
  }
];

function PromptsPanel({ onSelectPrompt }) {
  const allIndices = PROMPT_CATEGORIES.map((_, i) => i);
  const [openCategories, setOpenCategories] = useState(allIndices);

  const toggleCategory = (idx) => {
    setOpenCategories(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  return (
    <div className="prompts-panel">
      <div className="prompts-panel-header">
        <span className="prompts-panel-title">Prompt Library</span>
        <span className="prompts-panel-subtitle">Helpful prompts for reference to help you get started.</span>
      </div>
      {PROMPT_CATEGORIES.map((cat, catIdx) => {
        const isOpen = openCategories.includes(catIdx);
        return (
          <div key={catIdx} className="prompts-category">
            <button
              className={`prompts-category-header ${isOpen ? 'open' : ''}`}
              onClick={() => toggleCategory(catIdx)}
            >
              <span className="prompts-category-title">{cat.title}</span>
              <svg
                className={`prompts-category-arrow ${isOpen ? 'open' : ''}`}
                width="12" height="12" viewBox="0 0 12 12" fill="none"
              >
                <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {isOpen && (
              <div className="prompts-list">
                {cat.prompts.map((prompt, pIdx) => (
                  <div
                    key={pIdx}
                    className="prompts-item"
                    onClick={() => onSelectPrompt?.(prompt)}
                  >
                    <svg className="prompts-item-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="prompts-item-text">{prompt}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Threads() {
  const { user } = useContext(UserContext);
  const { workspace } = useContext(WorkspaceContext);
  const { org } = useContext(OrgContext);
  const { 
    compiledForecast: forecast, 
    setCompiledForecast: setForecast,
    forecasts,
    setForecasts,
    components,
    setComponents,
    componentsLoading,
    currentForecast,
    setCurrentForecast,
    triggerForecastReload,
    setScenarios,
    setActiveScenario
  } = useContext(ForecastContext);
  const navigate = useNavigate();
  
  // Core loading/error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fadeState, setFadeState] = useState('fade-in');
  
  // Data sources
  const [dataSources, setDataSources] = useState([]);
  const [dataSourcesLoaded, setDataSourcesLoaded] = useState(false);
  
  // Forecast state now comes from ForecastContext (compiledForecast and components)
  // No local state needed anymore!
  const [componentsFetched, setComponentsFetched] = useState(false);
  
  // Thread & message state (for Copilot)
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [startedTyping, setStartedTyping] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [copilotPrefill, setCopilotPrefill] = useState('');
  const [showProcessingMessage, setShowProcessingMessage] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [streamingActionData, setStreamingActionData] = useState([]);
  
  // UI state
  const [isCopilotCondensed, setIsCopilotCondensed] = useState(false);
  const [copilotTab, setCopilotTab] = useState('chat');
  const [chatLayoutView, setChatLayoutView] = useState('canvas');
  const [forecastViewFade, setForecastViewFade] = useState('fade-in');
  const [showDataSourcePopup, setShowDataSourcePopup] = useState(false);
  const [schemas, setSchemas] = useState([]);
  const [showUsagePopup, setShowUsagePopup] = useState(false);
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  
  // Constants
  const MAX_INPUT_LENGTH = 3000;

  // Fall back to currentForecast metadata when compiledForecast isn't available
  // (e.g. not-started forecasts have no compiled data)
  const effectiveForecast = (currentForecast?.status === 'in-progress' || currentForecast?.status === 'rolling-forward' || currentForecast?.status === 'failed_rollforward')
    ? currentForecast
    : (forecast || currentForecast);

  // Determine current view based on forecast status
  const isCompleted = effectiveForecast?.status === 'completed';
  const isRollingForward = effectiveForecast?.status === 'rolling-forward';
  const isRollforwardFailed = effectiveForecast?.status === 'failed_rollforward';

  /////////////////// USE EFFECTS ///////////////////
  
  // Scroll to bottom of messages
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, thinking, loading]);

  // Fetch data sources on mount
  useEffect(() => {
    if (!user?.id || !workspace?.id) return;
    
    const fetchDataSources = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/users/get-data-sources/${user.id}/${workspace.id}`,
          { withCredentials: true }
        );
        setDataSources(response.data || []);
      } catch (error) {
        console.error('Error fetching data sources:', error);
        setDataSources([]);
      } finally {
        setDataSourcesLoaded(true);
      }
    };

    fetchDataSources();
  }, [user?.id, workspace?.id]);

  // Rolling-forward live status message (streamed via SSE)
  const [rollforwardStatus, setRollforwardStatus] = useState('');

  // SSE subscription for rolling-forward — streams live progress + listens for done
  useEffect(() => {
    if (!isRollingForward || !forecast?.id) return;

    setRollforwardStatus('');

    const url = `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/reasoning-stream/${forecast.id}`;
    const es = new EventSource(url, { withCredentials: true });

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'done') {
          es.close();
          handleBuildComplete({ success: data.success, forecastId: forecast.id });
          return;
        }
        if (data.message) {
          setRollforwardStatus(data.message);
        }
      } catch {
        // ignore non-JSON
      }
    };

    es.onerror = () => {
      es.close();
    };

    return () => {
      es.close();
    };
  }, [isRollingForward, forecast?.id]);

  // Sync thread with current forecast's threadId
  useEffect(() => {
    if (thinking) return;
    if (!user?.id || !workspace?.id) return;

    const forecastThreadId = currentForecast?.threadId;

    if (forecastThreadId) {
      if (thread?.id === forecastThreadId) {
        setLoading(false);
        return;
      }
      setThread({ id: forecastThreadId });
      setMessages([]);
    } else {
      if (thread !== null) {
        setThread(null);
        setMessages([]);
      }
      setShowGreeting(true);
    }

    setLoading(false);
  }, [currentForecast?.threadId, user?.id, workspace?.id, thinking]);

  // Forecast loading is now handled centrally in App.js
  // Just update componentsFetched flag when components are loaded
  useEffect(() => {
    if (components && components.length > 0) {
      setComponentsFetched(true);
    } else if (!forecast?.id) {
      setComponentsFetched(false);
    }
  }, [components, forecast?.id]);

  // Load messages when thread is available
  useEffect(() => {
    if (thinking) return;
    if (thread?.id && messages.length === 0) {
      setMessagesLoading(true);
      getMessages(thread.id, setMessages, setLoading, setShowGreeting, setMessagesLoading)
        .finally(() => setMessagesLoading(false));
    }
  }, [thread?.id, thinking, messages.length]);

  // Show/hide greeting based on messages
  useEffect(() => {
    if (loading || thinking) return;
    setShowGreeting(!(messages.length > 0));
  }, [messages, loading, thinking]);

  // Close plus menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPlusMenu && !event.target.closest('.action-item')) {
        setShowPlusMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showPlusMenu]);

  /////////////////// HANDLERS ///////////////////

  // Helper function to reload forecast (triggers App.js reload via context)
  const reloadForecast = async (forecastId) => {
    try {
      // If same forecast ID, just trigger a reload
      if (forecastId === currentForecast?.id) {
        triggerForecastReload();
        setComponentsFetched(false);
        return;
      }
      
      // Different forecast ID: fetch fresh metadata and set as currentForecast
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/list/${workspace.id}/${user.id}`,
        { withCredentials: true }
      );
      const forecastsData = response.data || [];
      const updatedForecast = forecastsData.find(f => f.id === forecastId);
      if (updatedForecast) {
        setCurrentForecast(updatedForecast); // This triggers reload in App.js
        setComponentsFetched(false);
      }
    } catch (error) {
      console.error('Error reloading forecast:', error);
    }
  };

  const messageHandlers = createMessageHandlers({
    user,
    workspace,
    thread,
    forecast,
    uploadedFile: null,
    componentsFetched,
    loadForecastComponents: reloadForecast,
    setMessages,
    setThinking,
    setStartedTyping,
    setShowProcessingMessage,
    setProcessingMessage,
    setStreamingActionData,
    setShowUsagePopup,
    setError,
    setForecast,
    setComponents,
    setThread,
    setInputValue: () => {},
    setShowGreeting,
    setUploadedFile,
    setIsUploadingFile: () => {},
    setIsBuildingForecast: () => {},
    setLayoutView: () => {},
    setComponentsFetched,
    setCurrentForecast,
    setForecasts,
    setScenarios,
    setActiveScenario,
    onChecklistUpdate: (checklist) => {
      setForecast(prev => prev ? { ...prev, checklist } : prev);
    }
  });

  const fileOperations = createFileOperations({
    setUploadedFile,
    setShowUploadPopup,
  });

  const handleQuestionnaireComplete = async (formData, options = {}) => {
    console.log('Questionnaire completed with data:', formData, options.isRetry ? '(RETRY)' : '');
    
    if (!effectiveForecast?.id || !workspace?.id) {
      console.error('Missing forecast or workspace for startForecast');
      return;
    }

    const previousCurrentForecast = currentForecast;
    const previousCompiledForecast = forecast;

    try {
      const optimisticForecast = {
        ...(currentForecast || effectiveForecast || {}),
        id: effectiveForecast.id,
        user_input: formData,
        status: 'in-progress',
        error: null
      };

      setCurrentForecast(optimisticForecast);
      setForecast(null);
      setForecasts(prev => Array.isArray(prev)
        ? prev.map(item => (
            item.id === effectiveForecast.id
              ? { ...item, ...optimisticForecast }
              : item
          ))
        : prev
      );

      // Save user input and configure calendar via startForecast endpoint
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/user_started_forecast`,
        {
          forecastId: effectiveForecast.id,
          workspaceId: workspace.id,
          userId: user?.id,
          userInput: formData,
          isRetry: options.isRetry || false
        },
        { withCredentials: true }
      );

      if (response.data?.success && response.data?.forecast) {
        // This endpoint returns metadata, not a compiled forecast.
        setCurrentForecast(response.data.forecast);
        setForecasts(prev => Array.isArray(prev)
          ? prev.map(item => (
              item.id === response.data.forecast.id
                ? { ...item, ...response.data.forecast }
                : item
            ))
          : prev
        );
        console.log('Forecast started successfully:', response.data.forecast);
      }
    } catch (error) {
      console.error('Error starting forecast:', error);
      setCurrentForecast(previousCurrentForecast);
      setForecast(previousCompiledForecast);
      setError('Failed to start forecast. Please try again.');
    }
  };

  const handleRetryRollforward = async () => {
    if (!effectiveForecast?.id || !workspace?.id || !user?.id) return;

    setCurrentForecast(prev => prev ? { ...prev, status: 'rolling-forward', error: null } : prev);
    if (typeof setForecasts === 'function' && effectiveForecast?.id) {
      setForecasts(prev => Array.isArray(prev)
        ? prev.map(item => item.id === effectiveForecast.id ? { ...item, status: 'rolling-forward', error: null } : item)
        : prev
      );
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/retry-rollforward`,
        { forecastId: effectiveForecast.id, userId: user.id, workspaceId: workspace.id },
        { withCredentials: true }
      );
    } catch (err) {
      console.error('Retry rollforward failed:', err);
      const errMsg = err?.response?.data?.error || 'Retry failed';
      setCurrentForecast(prev => prev ? { ...prev, status: 'failed_rollforward', error: errMsg } : prev);
      if (typeof setForecasts === 'function' && effectiveForecast?.id) {
        setForecasts(prev => Array.isArray(prev)
          ? prev.map(item => item.id === effectiveForecast.id ? { ...item, status: 'failed_rollforward', error: errMsg } : item)
          : prev
        );
      }
    }
  };

  // Called by ForecastQuestionnaire when the SSE stream sends a 'done' event
  const handleBuildComplete = async ({ success, forecastId }) => {
    console.log('Build complete event received:', { success, forecastId });
    try {
      // Re-fetch forecast list so we get fresh metadata (status, threadId, etc.)
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/list/${workspace.id}/${user.id}`,
        { withCredentials: true }
      );
      const forecastsData = response.data || [];
      const updatedForecast = forecastsData.find(f => f.id === (forecastId || currentForecast?.id));
      if (updatedForecast) {
        setCurrentForecast(updatedForecast); // Updates status → drives isCompleted / questionnaire view
      }
      // Force App.js to reload the compiled forecast (components, cash_json, etc.)
      triggerForecastReload();
      setComponentsFetched(false);
      // Reload chat messages so any thread notifications (e.g. rollforward tile) appear immediately
      if (thread?.id) {
        setMessagesLoading(true);
        getMessages(thread.id, setMessages, setLoading, setShowGreeting, setMessagesLoading)
          .finally(() => setMessagesLoading(false));
      }
    } catch (err) {
      console.error('Error reloading forecast after build complete:', err);
    }
  };

  const handleConnectDataSource = () => navigate('/data-sources');
  
  const handlePlusMenuClick = (e) => {
    e.stopPropagation();
    setShowPlusMenu(!showPlusMenu);
  };

  const handleUploadFile = (e) => {
    e.stopPropagation();
    setShowPlusMenu(false);
    setShowUploadPopup(true);
  };

  const handleSyncDataSource = (e) => {
    e.stopPropagation();
    setShowPlusMenu(false);
    navigate('/data-sources');
  };

  const handleToggleChatLayout = () => {
    setForecastViewFade('fade-out');
    setTimeout(() => {
      setChatLayoutView(v => v === 'canvas' ? 'full' : 'canvas');
      setForecastViewFade('fade-in');
    }, 280);
  };

  const handleClearAllMessages = async () => {
    if (!thread?.id) return;
    const confirmed = window.confirm('Are you sure you want to clear all messages in this thread?');
    if (!confirmed) return;
    try {
      await clearThread(thread.id);
    } catch (err) {
      console.error('Error clearing messages:', err);
    } finally {
      setMessages([]);
    }
  };

  /////////////////// RENDER HELPERS ///////////////////

  const renderCopilot = () => (
    <Copilot
      messages={messages}
      setMessages={setMessages}
      thread={thread}
      themeColor={getCurrentModeConfig('general').themeColor}
      messagesLoading={messagesLoading}
      onForecastSnippetClick={async (snippet) => {
        const forecastPayload = snippet && typeof snippet.forecast === 'object' ? snippet.forecast : {};
        const title = snippet?.title || forecastPayload.title || 'Untitled Forecast';
        const finalForecast = { ...forecastPayload, title };
        // Update context to trigger reload
        if (finalForecast?.id) {
          setCurrentForecast(finalForecast);
          setComponentsFetched(false);
        }
      }}
      createThread={async (firstMessage, newThreadId) => {
        // For forecast chats, skip REST thread creation — the backend's
        // chatWithAI auto-creates and links the thread to the forecast.
        // Returning a placeholder so the Copilot has a threadId to reference
        // until the real thread metadata arrives via the stream.
        if (currentForecast?.id) {
          const placeholder = { id: newThreadId, title: firstMessage.substring(0, 50) };
          setThread(placeholder);
          return placeholder;
        }
        const newThread = await messageHandlers.createThread(firstMessage, newThreadId);
        if (newThread) setThread(newThread);
        return newThread;
      }}
      sendMessage={async ({ message, threadId, researchModeOverride }) => {
        const file = uploadedFile;
        setUploadedFile(null);
        await messageHandlers.chatWithAssistant({ message, threadId, researchModeOverride, attachedFile: file || undefined });
      }}
      thinking={thinking}
      setThinking={setThinking}
      showProcessingMessage={showProcessingMessage}
      processingMessage={processingMessage}
      streamingActionData={streamingActionData}
      startedTyping={startedTyping}
      clearMessages={() => setMessages([])}
      layoutView={chatLayoutView}
      toggleLayoutView={handleToggleChatLayout}
      showAgentHeader={false}
      showClearButton={false}
      agentName={getCurrentModeConfig('general').agentName}
      inputText="Ask Anything..."
      prefillText={copilotPrefill}
      onConsumePrefill={() => setCopilotPrefill('')}
      onInputClick={() => {}}
      renderGreeting={() => renderGreeting(showGreeting, chatLayoutView, (ex) => setCopilotPrefill(ex))}
      renderAssistantExtras={({ msg, idx, isLast }) => (
        <MessageActions message={msg} isLastMessage={isLast} />
      )}
      renderInputTop={uploadedFile ? (
        <div className="uploaded-file-display">
          <div className="file-chip new-upload">
            <span className="file-icon">📄</span>
            <span className="file-name">{uploadedFile.name}</span>
            <button className="remove-file-btn" onClick={() => fileOperations.removeUploadedFile()}>×</button>
          </div>
        </div>
      ) : null}
      isSendDisabled={false}
      maxInputLength={MAX_INPUT_LENGTH}
      researchMode={getCurrentModeConfig('general').researchMode}
      showDeepResearchToggle={false}
      leftActions={(
        <>
          {shouldShowAction('general', 'plus') && (
            <div className="action-item">
              <PlusIcon className="icon add" onMouseDown={(e) => e.stopPropagation()} onClick={handlePlusMenuClick} />
              {showPlusMenu && (
                <div className="more-selection-menu">
                  <div className="more-option" onMouseDown={(e) => e.stopPropagation()} onClick={handleUploadFile}>
                    <span>Upload File</span>
                  </div>
                  <div className="more-option" onMouseDown={(e) => e.stopPropagation()} onClick={handleSyncDataSource}>
                    <span>Sync Data Source</span>
                  </div>
                </div>
              )}
            </div>
          )}
          <div
            className={`action-item label ${chatLayoutView === 'full' ? 'active' : ''}`}
            onClick={handleToggleChatLayout}
          >
            <p className="bank-text">{chatLayoutView === 'full' ? 'Show Forecast' : 'Expand Chat'}</p>
          </div>
        </>
      )}
    />
  );

  // Render the Questionnaire View (not-started, in-progress, or failed)
  // The ForecastQuestionnaire handles all states with its built-in progress bar
  const renderQuestionnaireView = () => (
    <ForecastQuestionnaire 
      onComplete={handleQuestionnaireComplete}
      onBuildComplete={handleBuildComplete}
      forecast={effectiveForecast}
      isBuilding={effectiveForecast?.status === 'in-progress'}
      isFailed={effectiveForecast?.status === 'failed'}
      initialData={effectiveForecast?.user_input}
    />
  );

  // Render the Rolling Forward View (matches buildForecast building state)
  const renderRollingForwardView = () => (
    <div className="rolling-forward-container">
      <div className="rolling-forward-state">
        <div className="rolling-forward-header">
          <div className="loading-squares">
            {[...Array(4)].map((_, i) => (
              <div key={i} className={`square square${i + 1}`} />
            ))}
          </div>
          <h2 className="rolling-forward-title">Rolling Forward...</h2>
          <p className="rolling-forward-description">Updating & refining your forecast. This may take a few minutes.</p>
        </div>
        {rollforwardStatus && (
          <div className="reasoning-card" key={rollforwardStatus}>
            <span className="reasoning-live-dot" />
            <p className="reasoning-status">{rollforwardStatus}</p>
          </div>
        )}
      </div>
    </div>
  );

  // Render the Rollforward Failed View
  const renderRollforwardFailedView = () => (
    <div className="rolling-forward-container">
      <div className="rolling-forward-state">
        <div className="failed-icon">!</div>
        <h2 className="rolling-forward-title">Rollforward Failed</h2>
        <p className="rolling-forward-description">
          Something went wrong while rolling forward your forecast. Your forecast has been restored to its previous state.
        </p>
        <button className="btn-retry" onClick={handleRetryRollforward}>
          Retry Rollforward
        </button>
      </div>
    </div>
  );

  // Render the Forecast View (completed)
  const renderForecastView = () => (
    <div className={`forecast-view ${chatLayoutView === 'full' ? 'chat-full-mode' : ''} ${forecastViewFade}`}>
      {chatLayoutView !== 'full' && (
        <div className="forecast-main">
          <Forecast
            forecast={forecast}
            setForecast={setForecast}
            components={components || []}
            showHeader={true}
            showExportPDF={true}
            componentsLoading={componentsLoading}
          />
        </div>
      )}
      <div className={`forecast-copilot ${isCopilotCondensed ? 'condensed' : ''} ${chatLayoutView === 'full' ? 'full-chat' : ''}`}>
        {chatLayoutView !== 'full' && (
          <div className="copilot-header">
            {!isCopilotCondensed ? (
              <>
                <div className="copilot-tabs">
                  <button
                    className={`copilot-tab ${copilotTab === 'chat' ? 'active' : ''}`}
                    onClick={() => setCopilotTab('chat')}
                  >
                    Chat
                  </button>
                  <button
                    className={`copilot-tab ${copilotTab === 'activity' ? 'active' : ''}`}
                    onClick={() => setCopilotTab('activity')}
                  >
                    Activity
                  </button>
                  {forecast?.checklist?.length > 0 && (
                    <button
                      className={`copilot-tab ${copilotTab === 'todos' ? 'active' : ''}`}
                      onClick={() => setCopilotTab('todos')}
                    >
                      To-Dos
                      {(() => {
                        const remaining = forecast.checklist.filter(i => !i.completed).length;
                        return remaining > 0 ? (
                          <span className="copilot-tab-badge">{remaining}</span>
                        ) : null;
                      })()}
                    </button>
                  )}
                  <button
                    className={`copilot-tab ${copilotTab === 'prompts' ? 'active' : ''}`}
                    onClick={() => setCopilotTab('prompts')}
                  >
                    Prompts
                  </button>
                </div>
                <div className="copilot-actions">
                  <button className="copilot-action" onClick={() => setIsCopilotCondensed(true)}>
                    <RightArrowIcon className="expand-icon-header" />
                  </button>
                </div>
              </>
            ) : (
              <button className="copilot-expand" onClick={() => setIsCopilotCondensed(false)}>
                <RightArrowIcon className="expand-icon" />
              </button>
            )}
          </div>
        )}
        {!isCopilotCondensed && copilotTab === 'chat' && (
          <div className="copilot-body">
            {renderCopilot()}
          </div>
        )}
        {!isCopilotCondensed && copilotTab === 'todos' && (
          <div className="copilot-body">
            <TodosPanel
              checklist={forecast.checklist || []}
              forecastId={forecast.id}
              onChecklistUpdate={(checklist) => setForecast(prev => prev ? { ...prev, checklist } : prev)}
            />
          </div>
        )}
        {!isCopilotCondensed && copilotTab === 'activity' && (
          <div className="copilot-body">
            <Activity forecastId={forecast?.id} />
          </div>
        )}
        {!isCopilotCondensed && copilotTab === 'prompts' && (
          <div className="copilot-body">
            <PromptsPanel onSelectPrompt={(prompt) => {
              setCopilotPrefill(prompt);
              setCopilotTab('chat');
            }} />
          </div>
        )}
      </div>
    </div>
  );

  /////////////////// MAIN RENDER ///////////////////

  // Loading state - show loading screen while initial data is loading
  // or while forecast is being loaded from context
  if (loading || !dataSourcesLoaded || !effectiveForecast) {
    return (
      <div className="wrapper threads-wrapper">
        <LoadingScreen message="Loading your forecast..." />
      </div>
    );
  }

  // Error state
  if (error && !thread) {
    return (
      <div className={`wrapper threads-wrapper ${fadeState}`}>
        <div className="error-container">
          Oops! Something went wrong.<br /><br />{error}
        </div>
      </div>
    );
  }

  // No data sources connected or still syncing
  const hasReadyDataSource = dataSources?.some(ds => ds.status === 'ready');
  const hasSyncingDataSource = dataSources?.some(ds => ds.status === 'syncing');
  if (!dataSources || dataSources.length === 0 || !hasReadyDataSource) {
    return (
      <div className={`wrapper threads-wrapper ${fadeState}`}>
        <div className="setup-prompt-container">
          <div className="setup-prompt">
            <DatabaseIcon className="icon" />
            {hasSyncingDataSource ? (
              <>
                <h2>Your data source is syncing</h2>
                <p>Your data source is currently syncing. This may take a few minutes.</p>
                <button className="create-forecast-btn" onClick={handleConnectDataSource}>
                  View Data Source
                </button>
              </>
            ) : (
              <>
                <h2>Connect a data source to get started</h2>
                <p>You'll need to connect your accounting software or upload data before building a forecast.</p>
                <button className="create-forecast-btn" onClick={handleConnectDataSource}>
                  Connect Data Source
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main content: Questionnaire or Forecast view based on forecast status
  return (
    <div className={`wrapper threads-wrapper ${fadeState} ${isRollingForward || isRollforwardFailed ? 'rolling-forward-mode' : isCompleted ? 'forecast-mode' : 'questionnaire-mode'}`}>
      {/* Popups */}
      {showDataSourcePopup && (
        <DataPreview 
          userId={user.id} 
          workspaceId={workspace.id} 
          onClose={setShowDataSourcePopup} 
          schemas={schemas} 
          setSchemas={setSchemas} 
        />
      )}
      {showUsagePopup && (
        <UsagePopup 
          message="Threads Limit Reached" 
          submessage="You have reached your limit. Please upgrade your plan to add more threads!" 
          onClose={() => setShowUsagePopup(false)} 
        />
      )}
      {showUploadPopup && (
        <div className="popup-overlay" onClick={() => setShowUploadPopup(false)}>
          <div className="upload-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Upload File</h3>
              <button className="close-btn" onClick={() => setShowUploadPopup(false)}>×</button>
            </div>
            <div 
              className="upload-area"
              onDrop={fileOperations.handleFileDrop}
              onDragOver={fileOperations.handleDragOver}
            >
              <div className="upload-content">
                <p>Drag and drop your file here</p>
                <p className="upload-hint">PDF, Excel (.xlsx/.xls), or CSV</p>
                <p>or</p>
                <input 
                  type="file" 
                  accept=".xlsx,.xls,.pdf,.csv" 
                  onChange={fileOperations.handleFileUpload}
                  style={{ display: 'none' }}
                  id="upload-input"
                />
                <label htmlFor="upload-input" className="upload-btn">
                  Choose File
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main View */}
      {isRollingForward
        ? renderRollingForwardView()
        : isRollforwardFailed
          ? renderRollforwardFailedView()
          : isCompleted
            ? renderForecastView()
            : renderQuestionnaireView()}
    </div>
  );
}

export default Threads;
